import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router";
import type { ScreenPairingDto, ScreenPairingStatusDto } from "@klantpunt/shared";
import { BrandMark } from "../app/BrandMark";
import { api, ApiError } from "../lib/api";
import { useScreenTheme, useWakeLock } from "./ScreenPublic";

const POLL_MS = 3_000;
/** Het schermtoken na koppelen; zo opent de tv na een herstart meteen het dashboard. */
export const SCREEN_TOKEN_KEY = "klantpunt.screen";

export const storedScreenToken = () => {
  try {
    return localStorage.getItem(SCREEN_TOKEN_KEY);
  } catch {
    return null;
  }
};
export const forgetScreenToken = () => {
  try {
    localStorage.removeItem(SCREEN_TOKEN_KEY);
  } catch {
    /* niets te vergeten */
  }
};

/**
 * Koppelen met een code (app.klantpunt.io/scherm, docs/SCHERMEN.md §1a): de tv toont een code van 6 tekens,
 * de beheerder vult die in bij Gebouw › Schermen en de tv schakelt vanzelf over naar het dashboard.
 */
export function ScreenPairPage() {
  const navigate = useNavigate();
  useWakeLock();
  useScreenTheme();
  const [saved] = useState(storedScreenToken);
  useEffect(() => {
    if (saved) navigate(`/s/${saved}`, { replace: true });
  }, [saved, navigate]);

  const pairing = useQuery({
    queryKey: ["screen-pairing"],
    queryFn: () => api<ScreenPairingDto>("/public/screen-pairing", { method: "POST" }),
    enabled: !saved,
    staleTime: Infinity,
    retry: true,
    retryDelay: 10_000,
    // Vlak voor het verlopen een nieuwe code.
    refetchInterval: (q) => (q.state.data ? new Date(q.state.data.expiresAt).getTime() - Date.now() - 5_000 : false),
  });
  const p = pairing.data;
  const status = useQuery({
    queryKey: ["screen-pairing-status", p?.secret],
    queryFn: () =>
      api<ScreenPairingStatusDto>("/public/screen-pairing/status", { method: "POST", body: { secret: p!.secret } }),
    enabled: Boolean(p),
    refetchInterval: POLL_MS,
    refetchIntervalInBackground: true,
    retry: false,
  });
  useEffect(() => {
    const s = status.data;
    if (s?.state === "paired") {
      try {
        localStorage.setItem(SCREEN_TOKEN_KEY, s.token);
      } catch {
        /* zonder opslag werkt het ook, alleen niet na een herstart */
      }
      navigate(`/s/${s.token}`, { replace: true });
    }
  }, [status.data, navigate]);
  // Verlopen (of de server is herstart): nieuwe code.
  const expired = status.error instanceof ApiError && status.error.status === 404;
  const { refetch } = pairing;
  useEffect(() => {
    if (expired) void refetch();
  }, [expired, refetch]);

  useEffect(() => {
    document.title = "Scherm koppelen · Klantpunt";
  }, []);

  return (
    <div className="scr scr-empty scr-pair">
      <BrandMark size={56} />
      <h1 className="dot">Scherm koppelen</h1>
      {p ? (
        <>
          <p className="muted">
            Ga in Klantpunt naar het gebouw, tabblad <strong>Schermen</strong>, kies <strong>+ Scherm</strong> en vul
            deze code in:
          </p>
          <p className="scr-code" aria-label={`Code ${p.code.split("").join(" ")}`}>
            {p.code.slice(0, 3)}
            <span aria-hidden="true"> </span>
            {p.code.slice(3)}
          </p>
          <p className="muted small" role="status">
            Wacht op koppelen… De code verandert vanzelf na{" "}
            {new Date(p.expiresAt).toLocaleTimeString("nl-NL", { hour: "2-digit", minute: "2-digit" })}.
          </p>
        </>
      ) : pairing.isError ? (
        <p className="muted" role="status">
          Geen verbinding. We proberen het zo opnieuw.
        </p>
      ) : (
        <p className="muted" role="status">
          Code ophalen…
        </p>
      )}
    </div>
  );
}
